import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface PopularDestinationsProps {
  /** Handler for when a destination is clicked */
  onDestinationClick: (query: string) => void;
  /** Additional CSS classes for styling */
  className?: string;
}

interface Destination {
  name: string;
  image: string;
  query?: string;
  description: string;
}

interface DestinationCardProps {
  destination: Destination;
  index: number;
  isComingSoon?: boolean;
}

/**
 * Get optimized image URL based on size
 */
const getOptimizedImageUrl = (imagePath: string, size: 'thumbnail' | 'small' | 'medium' | 'large' = 'medium') => {
  try {
    const basePath = imagePath.substring(0, imagePath.lastIndexOf('.'));
    const fileName = basePath.split('/').pop();
    return `/images/destinations/optimized/${fileName}-${size}.jpg`;
  } catch {
    return imagePath;
  }
};

/**
 * Optimized image loading component with blur-up effect
 */
const OptimizedImage = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);

  // Generate optimized image URLs
  const thumbnailSrc = getOptimizedImageUrl(src, 'thumbnail');
  const smallSrc = getOptimizedImageUrl(src, 'small');
  const mediumSrc = getOptimizedImageUrl(src, 'medium');
  const largeSrc = getOptimizedImageUrl(src, 'large');

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.target as HTMLImageElement;
    console.error('Image load error:', {
      src: img.src,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      loadAttempts
    });
    setError(true);
    setLoadAttempts(prev => prev + 1);
  };

  return (
    <div className="relative w-full h-full">
      {/* Thumbnail image for blur-up effect */}
      <img
        src={thumbnailSrc}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center blur-xl scale-105 transition-opacity duration-300"
        style={{ opacity: isLoaded ? 0 : 1 }}
        onError={handleError}
      />
      
      {/* Main image with srcset for responsive loading */}
      <img 
        src={mediumSrc}
        srcSet={`
          ${smallSrc} 640w,
          ${mediumSrc} 1280w,
          ${largeSrc} 1920w
        `}
        sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
        alt={alt}
        className={cn(
          "absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700",
          isLoaded ? "opacity-100" : "opacity-0"
        )}
        onLoad={() => setIsLoaded(true)}
        onError={handleError}
        loading="lazy"
      />

      {/* Loading state */}
      <AnimatePresence>
        {!isLoaded && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-100 animate-pulse"
          />
        )}
      </AnimatePresence>

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 bg-slate-100 flex items-center justify-center">
          <span className="text-slate-400">Failed to load image</span>
        </div>
      )}
    </div>
  );
};

/**
 * PopularDestinations Component
 * 
 * Displays available and coming soon wildlife destinations with hover animations
 * and click handling. Extracted from HomePage for better maintainability.
 */
export function PopularDestinations({ 
  onDestinationClick, 
  className = ""
}: PopularDestinationsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('public', { keyPrefix: 'homepage.popularDestinations' });

  // Available destinations
  const AVAILABLE_DESTINATIONS = [
    { 
      name: t('destinations.norway.name'), 
      image: '/images/destinations/norway-fjords.jpg',
      query: 'Norway', 
      description: t('destinations.norway.description') 
    },
    {
      name: t('destinations.sweden.name'),
      image: '/images/destinations/sweden-lapland.jpg',
      query: 'Sweden',
      description: t('destinations.sweden.description')
    },
    { 
      name: t('destinations.spain.name'), 
      image: '/images/destinations/spain-picos.jpg',
      query: 'Spain', 
      description: t('destinations.spain.description') 
    }
  ];

  // Coming soon destinations
  const COMING_SOON_DESTINATIONS = [
    { 
      name: t('destinations.mexico.name'), 
      image: '/images/destinations/mexico-forest.jpg',
      description: t('destinations.mexico.description') 
    },
    { 
      name: t('destinations.indonesia.name'), 
      image: '/images/destinations/indonesia-komodo.jpg',
      description: t('destinations.indonesia.description') 
    },
    { 
      name: t('destinations.thailand.name'), 
      image: '/images/destinations/thailand-khao-yai.jpg',
      description: t('destinations.thailand.description') 
    }
  ];

  const DestinationCard = ({ 
    destination, 
    index, 
    isComingSoon = false 
  }: DestinationCardProps) => (
    <motion.div
      key={destination.name}
      className={cn(
        "relative rounded-xl overflow-hidden aspect-[16/9] group shadow-lg shadow-slate-900/8 transition-all duration-300",
        isComingSoon ? "cursor-default" : "cursor-pointer hover:shadow-xl hover:shadow-slate-900/12"
      )}
      whileHover={!isComingSoon ? { y: -8, scale: 1.02 } : {}}
      whileTap={!isComingSoon ? { scale: 0.98 } : {}}
      onClick={() => !isComingSoon && destination.query && onDestinationClick(destination.query)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: index * 0.1,
        ease: "easeOut"
      }}
    >
      {/* Optimized Image Component */}
      <OptimizedImage
        src={destination.image}
        alt={`${destination.name} wildlife destination`}
      />
      
      {/* Enhanced Gradient Overlay */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-t",
        isComingSoon 
          ? "from-black/90 via-black/60 to-black/30" 
          : "from-black/80 via-black/40 to-transparent"
      )} />
      
      {/* Coming Soon Badge */}
      {isComingSoon && (
        <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-2">
          <Clock className="w-4 h-4 text-white/90" />
          <span className="text-white/90 text-sm font-medium">{t('comingSoon')}</span>
        </div>
      )}
      
      {/* Destination Name and Description */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-t from-black/80 to-transparent pt-12">
        <h3 className="text-white text-lg sm:text-xl font-bold drop-shadow-lg mb-1">
          {destination.name}
        </h3>
        <p className="text-white/80 text-sm font-medium line-clamp-2">
          {destination.description}
        </p>
      </div>
    </motion.div>
  );

  return (
    <section className={`py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white ${className}`}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-10 sm:mb-12 gap-4">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
              {t('title')}
            </h2>
            <p className="text-lg text-slate-600 mt-2">
              {t('subtitle')}
            </p>
          </div>
        </div>
        
        {/* Available Destinations */}
        <div className="space-y-12">
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-6">
              {t('availableNow')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {AVAILABLE_DESTINATIONS.map((destination, index) => (
                <DestinationCard 
                  key={destination.name}
                  destination={destination} 
                  index={index} 
                />
              ))}
            </div>
          </div>

          {/* Coming Soon Destinations */}
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
              {t('comingSoonTitle')}
              <span className="inline-flex items-center justify-center bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full">
                <Clock className="w-4 h-4 mr-1" />
                {t('comingSoonBadge')}
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {COMING_SOON_DESTINATIONS.map((destination, index) => (
                <DestinationCard 
                  key={destination.name}
                  destination={destination} 
                  index={index} 
                  isComingSoon={true}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 